/**
 * Chat messages section wrapping the virtualized message list.
 * Shows a welcome screen when the conversation is new and has no messages.
 */
import type { ReactNode } from 'react'

import { ChatVirtualList } from '@/components/chat/chat-virtual-list.organism'
import { ChatWelcomeScreen } from '@/components/chat/chat-welcome-screen.organism'
import { Logo } from '@/components/layout/logo.atom'

import type { ChatViewHandlers, ChatViewState } from './chat-view-state.helper'
import { useChatWelcome } from './use-chat-welcome.hook'

interface ChatMessagesSectionProps {
  state: ChatViewState
  handlers: ChatViewHandlers
}

export const ChatMessagesSection = ({ state, handlers }: ChatMessagesSectionProps): ReactNode => {
  const isWelcomeVisible = state.isNewConversation && state.itemList.length === 0
  const welcome = useChatWelcome(state.agencySlug, isWelcomeVisible)

  return (
    <div className="chat-messages-area flex-1 overflow-hidden">
      {isWelcomeVisible ? (
        <ChatWelcomeScreen
          title={welcome.title}
          description={welcome.description}
          quickActionList={welcome.quickActionList}
          chatHint={welcome.chatHint}
          logoElement={
            welcome.isLogoVisible ? <Logo className="h-10" alt="SAAGA Solve" /> : undefined
          }
        />
      ) : (
        <ChatVirtualList
          itemList={state.itemList}
          currentUserId={state.currentUserId}
          conversationId={state.conversationId ?? undefined}
          pinnedItemIdList={state.pinnedMessageList.map((pin) => pin.id)}
          highlightedMessageId={state.highlightedMessageId}
          virtualListBinding={state.virtualListBinding}
          isFollowOutputEnabled={true}
          isAnimationEnabled={true}
          onToolCallClick={handlers.handleToolCallClick}
          onMessageClick={handlers.handleMessageClick}
          onContentBriefClick={handlers.handleContentBriefClick}
          onUserInteractionClick={handlers.handleUserInteractionClick}
          onReportClick={handlers.handleReportClick}
          onPinItem={handlers.handlePinItem}
          onAttachmentDownload={handlers.handleAttachmentDownload}
          onLoadMore={state.loadMoreMessages}
          hasMoreMessages={state.hasMoreMessages}
          isFetchingMoreMessages={state.isFetchingMoreMessages}
          className="scrollbar-thin"
        />
      )}
    </div>
  )
}
